import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  Search,
  Filter,
  Eye,
  X,
  Sheet,
  FileText,
  ChevronDown,
  Download,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { ReportFilterParams } from "@/types";
import { useTranslation } from "react-i18next";

interface ReportFiltersProps {
  filters: ReportFilterParams;
  handleFilterChange: (key: keyof ReportFilterParams, value: string) => void;
  activeFilters: string[];
  clearFilter: (key: keyof ReportFilterParams) => void;
  onPreviewData: () => void;
  handleExportPDF: () => void;
  handleExportExcel: () => void;
  exportLoading: "pdf" | "excel" | null;
  exportDropdownOpen: boolean;
  setExportDropdownOpen: (open: boolean) => void;
  loading?: boolean;
  totalRecords?: number;
  onRemoveAllFilters: () => void;
}

export function ReportFilters({
  filters,
  handleFilterChange,
  activeFilters,
  clearFilter,
  onPreviewData,
  handleExportPDF,
  handleExportExcel,
  exportLoading,
  exportDropdownOpen,
  setExportDropdownOpen,
  loading: previewLoading = false,
  totalRecords = 0,
  onRemoveAllFilters,
}: ReportFiltersProps) {
  const { t } = useTranslation();

  const handlePreviewClick = async () => {
    onPreviewData();
  };

  // Remove all filters handler now calls parent
  const handleRemoveAllFilters = () => {
    onRemoveAllFilters();
  };

  const getFilterLabel = (key: string, value: string): string => {
    switch (key) {
      case "startDate":
        return `${t("ReportFilters.FilterLabels.From")}: ${new Date(value).toLocaleDateString()}`;
      case "endDate":
        return `${t("ReportFilters.FilterLabels.To")}: ${new Date(value).toLocaleDateString()}`;
      case "orderType":
        return `${t("ReportFilters.FilterLabels.Type")}: ${value.replace("_", " ")}`;
      case "status":
        return `${t("ReportFilters.FilterLabels.Status")}: ${value}`;
      case "minAmount":
        return `${t("ReportFilters.FilterLabels.Min")}: ${formatCurrency(Number(value))}`;
      case "maxAmount":
        return `${t("ReportFilters.FilterLabels.Max")}: ${formatCurrency(Number(value))}`;
      case "searchTerm":
        return `${t("ReportFilters.FilterLabels.Search")}: "${value}"`;
      default:
        return `${key}: ${value}`;
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="w-5 h-5" />
          {t("ReportFilters.Title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Date Range Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="startDate" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t("ReportFilters.DateRange.StartDate")}
            </Label>
            <Input
              id="startDate"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="endDate" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t("ReportFilters.DateRange.EndDate")}
            </Label>
            <Input
              id="endDate"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              min={filters.startDate}
              className="w-full"
            />
          </div>
        </div>

        {/* Order Type and Status Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("ReportFilters.OrderType.Label")}</Label>
            <Select
              value={filters.orderType || "all"}
              onValueChange={(value) =>
                handleFilterChange("orderType", value === "all" ? "" : value)
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("ReportFilters.OrderType.Placeholder")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("ReportFilters.OrderType.AllTypes")}
                </SelectItem>
                <SelectItem value="walk_in">
                  {t("Counter.AppointmentTypes.WalkIn")}
                </SelectItem>
                <SelectItem value="scheduled">
                  {t("Counter.AppointmentTypes.Scheduled")}
                </SelectItem>
                <SelectItem value="emergency">
                  {t("Counter.AppointmentTypes.Emergency")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("ReportFilters.OrderStatus.Label")}</Label>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                handleFilterChange("status", value === "all" ? "" : value)
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("ReportFilters.OrderStatus.Placeholder")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("ReportFilters.OrderStatus.AllStatuses")}
                </SelectItem>
                <SelectItem value="confirmed">
                  {t("ReportFilters.OrderStatus.Confirmed")}
                </SelectItem>
                {/* <SelectItem value="served">
                  {t("ReportFilters.OrderStatus.Served")}
                </SelectItem> */}
                <SelectItem value="completed">
                  {t("ReportFilters.OrderStatus.Completed")}
                </SelectItem>
                <SelectItem value="cancelled">
                  {t("ReportFilters.OrderStatus.Cancelled")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Amount Range  */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="minAmount">
              {t("ReportFilters.AmountRange.MinLabel")}
            </Label>
            <span className="text-xs text-muted-foreground">
              {t("ReportFilters.AmountRange.MinHelper")}
            </span>
            <Input
              id="minAmount"
              type="number"
              placeholder="0.00"
              value={filters.minAmount || ""}
              onChange={(e) => handleFilterChange("minAmount", e.target.value)}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="maxAmount">
              {t("ReportFilters.AmountRange.MaxLabel")}
            </Label>
            <span className="text-xs text-muted-foreground">
              {t("ReportFilters.AmountRange.MaxHelper")}
            </span>
            <Input
              id="maxAmount"
              type="number"
              placeholder="0.00"
              value={filters.maxAmount || ""}
              onChange={(e) => handleFilterChange("maxAmount", e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Search Term */}
        <div className="space-y-2">
          <Label htmlFor="searchTerm" className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            {t("ReportFilters.Search.Label")}
          </Label>
          <Input
            id="searchTerm"
            placeholder={t("ReportFilters.Search.Placeholder")}
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
            className="w-full"
          />
        </div>

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filterKey) => {
                const value = filters[filterKey as keyof ReportFilterParams];
                if (!value) return null;

                return (
                  <Badge
                    key={filterKey}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    <span className="text-xs">
                      {getFilterLabel(filterKey, String(value))}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        clearFilter(filterKey as keyof ReportFilterParams)
                      }
                      className="h-4 w-4 p-0 hover:bg-transparent"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <div className="flex gap-3">
            <Button
              onClick={handlePreviewClick}
              disabled={previewLoading}
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {previewLoading
                ? t("ReportFilters.Actions.Loading")
                : `${t("ReportFilters.Actions.PreviewData")}${totalRecords > 0 ? ` (${totalRecords})` : ""}`}
            </Button>
            {activeFilters.length > 0 && (
              <Button
                variant="outline"
                onClick={handleRemoveAllFilters}
                className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
              >
                <X className="w-4 h-4" />
                {t("ReportFilters.Actions.RemoveAllFilters")}
              </Button>
            )}
          </div>
          <div className="relative w-full sm:w-auto">
            <button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-white border-2 border-black text-black rounded-lg hover:bg-black hover:text-white transition-all duration-200 w-full sm:w-auto cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">
                {t("ReportFilters.Export.ButtonText")}
              </span>
              <span className="sm:hidden">
                {t("ReportFilters.Export.ButtonTextShort")}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  exportDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {exportDropdownOpen && (
              <div className="absolute right-0 sm:right-0 mt-2 w-64 sm:w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 min-w-max">
                <div className="py-2">
                  <button
                    onClick={handleExportPDF}
                    disabled={exportLoading !== null}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {exportLoading === "pdf" ? (
                      <div className="w-5 h-5 flex-shrink-0">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-500 border-t-transparent"></div>
                      </div>
                    ) : (
                      <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                    )}
                    <div className="text-left flex-1">
                      <div className="font-medium">
                        {exportLoading === "pdf"
                          ? t("ReportFilters.Export.GeneratingPDF")
                          : t("ReportFilters.Export.ExportAsPDF")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t("ReportFilters.Export.PDFDescription")}
                      </div>
                    </div>
                  </button>

                  <div className="h-px bg-gray-100 mx-2"></div>

                  <button
                    onClick={handleExportExcel}
                    disabled={exportLoading !== null}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {exportLoading === "excel" ? (
                      <div className="w-5 h-5 flex-shrink-0">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-green-500 border-t-transparent"></div>
                      </div>
                    ) : (
                      <Sheet className="w-5 h-5 text-green-500 flex-shrink-0" />
                    )}
                    <div className="text-left flex-1">
                      <div className="font-medium">
                        {exportLoading === "excel"
                          ? t("ReportFilters.Export.GeneratingExcel")
                          : t("ReportFilters.Export.ExportAsExcel")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t("ReportFilters.Export.ExcelDescription")}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Backdrop for mobile */}
          {exportDropdownOpen && (
            <div
              className="fixed inset-0 z-40 bg-transparent bg-opacity-10 sm:bg-transparent"
              onClick={() => setExportDropdownOpen(false)}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
